import { Play } from "lucide-react";
import { FaSpotify, FaAmazon } from "react-icons/fa";
import { SiIheartradio, SiYoutubemusic, SiApplepodcasts } from "react-icons/si";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, PODCAST as POD } from "../data/siteContent";
import { PODCAST_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, podcastSchema, webPageSchema, breadcrumbSchema } from "../seo/schemas";

const PlatformButton = ({ icon, label, color, href, tid }) => (
  <a
    data-testid={tid}
    href={href}
    target="_blank"
    rel="noreferrer"
    className="group flex items-center justify-between border border-black/15 hover:border-[color:var(--c)] px-5 py-4 transition-all"
    style={{ "--c": color }}
  >
    <span className="flex items-center gap-3">
      {icon}
      <span className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[#0a0a0a]">{label}</span>
    </span>
    <Play size={14} className="text-black/40 group-hover:text-[color:var(--c)]" />
  </a>
);

export default function Podcast() {
  return (
    <SiteLayout>
      <Seo
        title="The Prove Your Point Podcast — Hosted by Manuel Santana"
        description="Long conversations on relationships, culture, and the things we won't say out loud. Hosted by Manuel Santana. Listen on Spotify, Apple Podcasts and YouTube."
        path="/podcast"
        schemas={buildSchemas([
          podcastSchema,
          webPageSchema({ path: "/podcast", name: "The Prove Your Point Podcast", description: "Episodes, trailer and listening links for The Prove Your Point Podcast." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Podcast", path: "/podcast" }]),
        ])}
      />
      <PageHeader
        overline="Podcast"
        title="The Prove Your"
        italic="Point Podcast."
        kicker="Long conversations on relationships, culture, and the things we won't say out loud. New episode every Tuesday."
      />

      <section data-testid={PODCAST_T.section} className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="portrait-frame w-full aspect-video relative">
              <img src={ASSETS.podcastPortrait} alt="The Prove Your Point Podcast" draggable={false} />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white text-center px-6">
                <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#C8102E] mb-3">— Trailer</p>
                <p className="font-display text-2xl md:text-3xl">Coming soon.</p>
                <p className="mt-3 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/70">In the meantime — listen on the platforms →</p>
              </div>
            </div>
            <p className="mt-4 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Show trailer coming soon</p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <h2 className="font-display text-[#0a0a0a] text-3xl md:text-5xl tracking-tight leading-tight">
              Listen anywhere.
            </h2>
            <p className="mt-4 text-black/65 text-base md:text-lg leading-relaxed">{POD.tagline}</p>
            <div className="mt-8 flex flex-col gap-3">
              <PlatformButton tid={PODCAST_T.spotify} icon={<FaSpotify className="text-[#1DB954]" size={18} />} label="Listen on Spotify" color="#1DB954" href={POD.spotify} />
              <PlatformButton tid={PODCAST_T.apple} icon={<SiApplepodcasts className="text-[#9933CC]" size={18} />} label="Listen on Apple Podcasts" color="#9933CC" href={POD.apple} />
              <PlatformButton tid="podcast-cta-iheart" icon={<SiIheartradio className="text-[#C6002B]" size={18} />} label="Listen on iHeart" color="#C6002B" href={POD.iheart} />
              <PlatformButton tid="podcast-cta-amazon-music" icon={<FaAmazon className="text-[#00A8E1]" size={18} />} label="Listen on Amazon Music" color="#00A8E1" href={POD.amazonMusic} />
              <PlatformButton tid={PODCAST_T.youtube} icon={<SiYoutubemusic className="text-[#FF0000]" size={20} />} label="Listen on YouTube Music" color="#FF0000" href={POD.youtubeMusic} />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
